import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faClock, faHeart, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingAIButton from "./components/FloatingAIButton";
import destinationHero from "./assests/destination.png";
import Modal from "./Modal";
import API_BASE_URL from "./config/api";

// --- Keyframes ---
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- Styled Components ---

const PageContainer = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: "Poppins", sans-serif;
`;

const HeroSection = styled.header`
  width: 100%;
  height: 40vh;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 30vh;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  filter: brightness(0.6);
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.6) 100%);
`;

const HeroTitle = styled.h1`
  font-family: "MonteCarlo", cursive;
  color: white;
  font-size: 6rem;
  text-shadow: 0 4px 15px rgba(0,0,0,0.8);
  animation: ${fadeIn} 1s ease-out;
  text-align: center;
  background: rgba(0, 0, 0, 0.3);
  padding: 10px 40px;
  border-radius: 50px;
  backdrop-filter: blur(2px);
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    font-size: 4rem;
  }
`;

const MainContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
  display: flex;
  gap: 30px;

  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.aside`
  width: 300px;
  flex-shrink: 0;
  background: white;
  padding: 25px;
  border-radius: 20px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  height: fit-content;

  @media (max-width: 992px) {
    width: 100%;
  }
`;

const SidebarTitle = styled.h3`
  font-family: "Merriweather", serif;
  font-size: 1.5rem;
  color: #02495B;
  margin-bottom: 20px;
  border-bottom: 2px solid #eee;
  padding-bottom: 10px;
`;

const FilterSection = styled.div`
  margin-bottom: 25px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 15px;

  &:last-child {
    border-bottom: none;
  }
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-family: "Merriweather", serif;
  font-weight: 700;
  color: #02495B;
  font-size: 1.1rem;
  margin-bottom: 15px;
  transition: color 0.3s;

  &:hover {
    color: #1CA8CB;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  color: #555;
  font-size: 0.95rem;
  transition: all 0.2s;

  &:hover {
    color: #1CA8CB;
  }

  input {
    accent-color: #1CA8CB;
    width: 16px;
    height: 16px;
  }
`;

const RangeContainer = styled.div`
  padding: 0 5px;
`;

const RangeInput = styled.input`
  width: 100%;
  height: 6px;
  background: #e0e0e0;
  border-radius: 5px;
  outline: none;
  -webkit-appearance: none;
  margin: 10px 0;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #1CA8CB;
    cursor: pointer;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  }
`;

const RangeLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #777;
  margin-top: 5px;
`;

const RangeValue = styled.p`
  font-weight: 600;
  color: #1CA8CB;
  text-align: center;
  margin-bottom: 5px;
`;

const ContentArea = styled.main`
  flex-grow: 1;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  background: white;
  padding: 10px;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const SearchInput = styled.input`
  flex-grow: 1;
  border: none;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 1rem;
  background: #f8f9fa;
  color: #333;
  outline: none;

  &:focus {
    box-shadow: 0 0 0 2px rgba(28, 168, 203, 0.2);
  }
`;

const SearchButton = styled.button`
  background: #02495B;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: #1CA8CB;
  }
`;

const PackageCard = styled.div`
  display: flex;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 25px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
  transition: all 0.3s ease;
  border: 1px solid transparent;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0,0,0,0.1);
    border-color: rgba(28, 168, 203, 0.3);
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CardImageContainer = styled.div`
  width: 350px;
  position: relative;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    height: 250px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeartIcon = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background: white;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: transform 0.2s;
  z-index: 2;

  &:hover {
    transform: scale(1.1);
  }
`;

const CardContent = styled.div`
  padding: 25px;
  flex-grow: 1;
  display: flex;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const CardInfo = styled.div`
  flex: 1;
`;

const CardTitle = styled.h2`
  font-family: "Merriweather", serif;
  font-size: 1.5rem;
  color: #02495B;
  margin-bottom: 10px;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  color: #666;
  font-size: 0.9rem;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    color: #1CA8CB;
  }
`;

const CardDescription = styled.p`
  color: #555;
  line-height: 1.6;
  margin-bottom: 15px;
  font-size: 0.95rem;
`;

const CardHighlight = styled.div`
  background: #e9f6f9;
  color: #02495B;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.9rem;
  display: inline-block;
  font-weight: 500;
`;

const CardPriceSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  min-width: 180px;
  border-left: 1px solid #eee;
  padding-left: 20px;

  @media (max-width: 992px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border-left: none;
    border-top: 1px solid #eee;
    padding-left: 0;
    padding-top: 20px;
    margin-top: 10px;
  }
`;

const PriceTag = styled.div`
  font-family: "Merriweather", serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #02495B;
  margin-bottom: 15px;

  @media (max-width: 992px) {
    margin-bottom: 0;
    font-size: 1.5rem;
  }
`;

const ViewTripButton = styled.button`
  background: #1CA8CB;
  color: white;
  border: none;
  padding: 12px 25px;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 10px rgba(28, 168, 203, 0.3);

  &:hover {
    background: #004E61;
    transform: translateY(-2px);
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 40px;
`;

const PageButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${props => (props.active ? "#02495B" : "white")};
  color: ${props => (props.active ? "white" : "#555")};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);

  &:hover {
    background: ${props => (props.active ? "#02495B" : "#e9f6f9")};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  gap: 20px;
  background: #f8f9fa;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #ddd;
  border-top: 4px solid #1CA8CB;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;



// --- Component Logic ---

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function DestinationPackages() {
  const query = useQuery();
  const countryName = query.get("country");
  const navigate = useNavigate();

  // State
  const [isModalOpen, setModalOpen] = useState(false);
  const [expandDestination, setExpandDestination] = useState(true);
  const [expandPrice, setExpandPrice] = useState(true);
  const [expandDuration, setExpandDuration] = useState(true);
  const [price, setPrice] = useState(100000);
  const [days, setDays] = useState(365);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(() => ({
    [countryName]: true,
  }));
  const [favoritePackages, setFavoritePackages] = useState({});

  const itemsPerPage = 10;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Handlers
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleViewTripClick = (packageSlug) => {
    navigate(`/ tripdetails ? name = ${packageSlug} `);
  };

  const toggleFavorite = (id) => {
    setFavoritePackages((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleDestination = (destination) => {
    setSelectedDestination((prev) => ({ ...prev, [destination]: !prev[destination] }));
  };

  const handlePriceChange = (event) => setPrice(event.target.value);
  const handleDaysChange = (event) => setDays(event.target.value);
  const handleSearchChange = (event) => setSearchTerm(event.target.value.toLowerCase());
  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  // Effects
  useEffect(() => {
    setIsLoading(true);
    fetch(`${API_BASE_URL}/packages`)
      .then((response) => response.json())
      .then((result) => {
        setPackages(result);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching packages:", error);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (countryName) {
      setSelectedDestination({ [countryName]: true });
    }
  }, [countryName]);

  // Helper to calculate package price dynamically (same formula as TripDetails)
  const calculatePackagePrice = (pkg) => {
    if (!pkg.itinerary) return pkg.totalPrice || 0;

    let overallBase = 0;

    pkg.itinerary.forEach(day => {
      // Hotels
      const hotelsTotal = (day.hotels || []).reduce((sum, hotel) => {
        let rate = 0;
        if (hotel.hotelId?.seasonalRates && hotel.hotelId.seasonalRates.length > 0) {
          const season = hotel.hotelId.seasonalRates[0];
          if (season.rates && season.rates.length > 0) {
            const matchingRate = season.rates.find(r => r.roomType === hotel.roomType);
            rate = matchingRate ? matchingRate.specialRateCPAI : season.rates[0].specialRateCPAI;
          }
        }
        const nights = hotel.nights || 0;
        return sum + (rate * nights);
      }, 0);

      // Cabs
      const cabsTotal = (day.cabs || []).reduce((sum, cab) =>
        sum + (cab.days * (cab.cabId?.pricePerDay || 0)), 0);

      // Activities
      const activitiesTotal = (day.destinations || []).reduce((destSum, dest) =>
        destSum + (dest.activities || []).reduce((actSum, activity) =>
          actSum + (activity.activityId?.price || 0), 0), 0);

      overallBase += hotelsTotal + cabsTotal + activitiesTotal;
    });

    // Apply NEW formula: Base + Service Charge (10%) + GST (18% on Service Charge)
    const serviceCharge = overallBase * 0.10;
    const gst = serviceCharge * 0.18;
    const finalTotal = overallBase + serviceCharge + gst;

    return Math.round(finalTotal);
  };

  // Filter Logic
  const filteredPackages = packages
    .filter((pkg) => pkg.isActive)
    .filter((pkg) => {
      // Use dynamically calculated price (same formula as TripDetails)
      const packagePrice = calculatePackagePrice(pkg);
      const packageDays = Math.floor(
        (new Date(pkg.endDate) - new Date(pkg.startDate)) / (1000 * 3600 * 24)
      );

      const selectedDestinations = Object.keys(selectedDestination).filter(
        (key) => selectedDestination[key]
      );

      const isDestinationMatching =
        selectedDestinations.length === 0 ||
        selectedDestinations.some((dest) =>
          pkg.itinerary?.some((day) =>
            day.destinations?.some(
              (destination) =>
                destination.destinationId?.name?.toLowerCase() === dest.toLowerCase()
            )
          )
        );

      const isPriceMatching = packagePrice <= price;
      const isDurationMatching = packageDays <= days || days === 365;
      const isSearchMatching = !searchTerm || pkg.name.toLowerCase().includes(searchTerm);

      return isPriceMatching && isDurationMatching && isSearchMatching && isDestinationMatching;
    });

  const totalPages = Math.ceil(filteredPackages.length / itemsPerPage);
  const currentPackages = filteredPackages.slice(startIndex, endIndex);

  // Helper for Duration String
  const getDurationString = (pkg) => {
    const startDate = new Date(pkg.startDate);
    const endDate = new Date(pkg.endDate);
    const timeDifference = endDate - startDate;
    const days = Math.floor(timeDifference / (1000 * 3600 * 24));

    if (days === 364) return "Jan to Dec";

    const startMonth = startDate.toLocaleString("default", { month: "short" });
    const endMonth = endDate.toLocaleString("default", { month: "short" });
    return `${startMonth} to ${endMonth}`;
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
        <p style={{ marginTop: 10, color: "#666" }}>Loading packages...</p>
      </LoadingContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />

      <HeroSection>
        <HeroImage src={destinationHero} alt="Destination Hero" />
        <HeroOverlay>
          <HeroTitle>{countryName || "All Packages"}</HeroTitle>
        </HeroOverlay>
      </HeroSection>

      <MainContainer>
        {/* Sidebar */}
        <Sidebar>
          <SidebarTitle>Criteria</SidebarTitle>

          <FilterSection>
            <FilterHeader onClick={() => setExpandDestination(!expandDestination)}>
              Destination
              <FontAwesomeIcon icon={expandDestination ? faChevronUp : faChevronDown} size="sm" />
            </FilterHeader>
            {expandDestination && (
              <div>
                {Array.from(
                  new Set(
                    packages.flatMap((pkg) =>
                      pkg.itinerary?.flatMap((day) =>
                        day.destinations?.map((d) => d.destinationId?.name)
                      )
                    )
                  )
                ).map((destinationName) => (
                  <CheckboxLabel key={destinationName}>
                    <input
                      type="checkbox"
                      checked={selectedDestination[destinationName] || false}
                      onChange={() => toggleDestination(destinationName)}
                    />
                    {destinationName}
                  </CheckboxLabel>
                ))}
              </div>
            )}
          </FilterSection>

          <FilterSection>
            <FilterHeader onClick={() => setExpandPrice(!expandPrice)}>
              Price
              <FontAwesomeIcon icon={expandPrice ? faChevronUp : faChevronDown} size="sm" />
            </FilterHeader>
            {expandPrice && (
              <RangeContainer>
                <RangeValue>Max Price: ₹{price}</RangeValue>
                <RangeInput
                  type="range"
                  min="1000"
                  max="100000"
                  value={price}
                  onChange={handlePriceChange}
                />
                <RangeLabels>
                  <span>₹1k</span>
                  <span>₹100k</span>
                </RangeLabels>
              </RangeContainer>
            )}
          </FilterSection>

          <FilterSection>
            <FilterHeader onClick={() => setExpandDuration(!expandDuration)}>
              Duration
              <FontAwesomeIcon icon={expandDuration ? faChevronUp : faChevronDown} size="sm" />
            </FilterHeader>
            {expandDuration && (
              <RangeContainer>
                <RangeValue>Max Days: {days}</RangeValue>
                <RangeInput
                  type="range"
                  min="2"
                  max="365"
                  value={days}
                  onChange={handleDaysChange}
                />
                <RangeLabels>
                  <span>2 Days</span>
                  <span>365 Days</span>
                </RangeLabels>
              </RangeContainer>
            )}
          </FilterSection>
        </Sidebar>

        {/* Content */}
        <ContentArea>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search for packages (e.g., Honeymoon, Adventure)..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <SearchButton>Search</SearchButton>
          </SearchContainer>

          <div>
            {currentPackages.length > 0 ? (
              currentPackages.map((pkg) => (
                <PackageCard key={pkg._id}>
                  <CardImageContainer>
                    <CardImage src={pkg.coverImage} alt={pkg.name} />
                    <HeartIcon onClick={() => toggleFavorite(pkg._id)}>
                      <FontAwesomeIcon
                        icon={faHeart}
                        color={favoritePackages[pkg._id] ? "#ff4d4d" : "#ccc"}
                      />
                    </HeartIcon>
                  </CardImageContainer>

                  <CardContent>
                    <CardInfo>
                      <CardTitle>{pkg.name}</CardTitle>

                      <CardMeta>
                        <MetaItem>
                          <FontAwesomeIcon icon={faLocationDot} />
                          {pkg.itinerary?.[0]?.destinations?.[0]?.destinationId?.name || "Multiple Destinations"}
                        </MetaItem>
                        <MetaItem>
                          <FontAwesomeIcon icon={faClock} />
                          {getDurationString(pkg)}
                        </MetaItem>
                      </CardMeta>

                      <CardDescription>
                        {pkg.description
                          ? pkg.description.split(" ").slice(0, 20).join(" ") + "..."
                          : "Explore this amazing package designed for an unforgettable experience."}
                      </CardDescription>

                      <CardHighlight>
                        {pkg.notes ? pkg.notes.slice(0, 30) + "..." : "Best Seller"}
                      </CardHighlight>
                    </CardInfo>

                    <CardPriceSection>
                      <PriceTag>₹{calculatePackagePrice(pkg).toLocaleString()}/-</PriceTag>
                      <ViewTripButton onClick={() => handleViewTripClick(pkg.slug)}>
                        View Details
                      </ViewTripButton>
                    </CardPriceSection>
                  </CardContent>
                </PackageCard>
              ))
            ) : (
              <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                <h3>No packages found matching your criteria.</h3>
                <p>Try adjusting filters or search terms.</p>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <Pagination>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                <PageButton
                  key={number}
                  active={currentPage === number}
                  onClick={() => handlePageChange(number)}
                >
                  {number}
                </PageButton>
              ))}
            </Pagination>
          )}
        </ContentArea>
      </MainContainer>

      <Footer />

      <FloatingAIButton onClick={openModal} />

      {isModalOpen && <Modal closeModal={closeModal} />}
    </PageContainer>
  );
}

export default DestinationPackages;